import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, TrendingDown, IndianRupee, Users, Target,
  Clock, BarChart3, PieChart, Mail, Send, Eye, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const PIPELINE_LABELS = {
  new_enquiry: 'New Enquiry', follow_up: 'Follow-up', quote_sent: 'Quote Sent',
  negotiation: 'Negotiation', booked: 'Booked', lost: 'Lost'
};

const SalesReportsPage = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchReports = async () => {
    try {
      const [reportsRes, emailRes] = await Promise.all([
        api.get('/sales/reports'),
        api.get('/email/status')
      ]);
      setReports(reportsRes.data);
      setEmailStatus(emailRes.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handlePreviewDigest = async () => {
    try {
      const res = await api.get('/email/digest-preview');
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  const handleSendDigest = async () => {
    setSending(true);
    try {
      const res = await api.post('/email/send-digest');
      if (res.data.resend_configured) {
        toast.success(`Digest sent to ${res.data.results.length} recipients`);
      } else {
        toast.info('Resend not configured. Add RESEND_API_KEY to .env to enable.');
      }
      const emailRes = await api.get('/email/status');
      setEmailStatus(emailRes.data);
    } catch (err) {
      toast.error('Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (a) => a ? `₹${Number(a).toLocaleString('en-IN')}` : '₹0';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div></div>;
  if (!reports) return null;

  const leadTrend = reports.this_month_leads - reports.last_month_leads;

  return (
    <div className="space-y-6" data-testid="sales-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Sales Reports</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {(user?.roles || [user?.role]).includes('sales_person') ? 'Your personal performance' : 'Team performance overview'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchReports}
          className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-sales-reports">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Daily Digest Card */}
      {(user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r)) && (
        <Card className="bg-[#121212] border-white/10" data-testid="digest-card">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#F5A623]/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-[#F5A623]" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Daily Sales Digest</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <div className={`h-1.5 w-1.5 rounded-full ${emailStatus?.configured ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                  {emailStatus?.configured ? 'Resend connected' : 'Resend not configured'}
                  {emailStatus?.last_digest_sent && (
                    <span>• Last sent: {new Date(emailStatus.last_digest_sent).toLocaleDateString('en-IN')}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-white/10 text-zinc-400 hover:bg-white/5" onClick={handlePreviewDigest} data-testid="preview-digest-btn">
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
              <Button size="sm" className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" onClick={handleSendDigest} disabled={sending} data-testid="send-digest-btn">
                <Send className="h-3.5 w-3.5 mr-1.5" /> {sending ? 'Sending...' : 'Send Now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Leads This Month</p>
              {leadTrend !== 0 && (
                <div className={`flex items-center gap-1 text-xs ${leadTrend > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {leadTrend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(leadTrend)}
                </div>
              )}
            </div>
            <p className="text-3xl font-semibold text-white">{reports.this_month_leads}</p>
            <p className="text-xs text-zinc-600 mt-1">vs {reports.last_month_leads} last month</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Conversion Rate</p>
            <p className={`text-3xl font-semibold ${reports.conversion_rate >= 30 ? 'text-emerald-400' : reports.conversion_rate >= 15 ? 'text-amber-400' : 'text-rose-400'}`}>
              {reports.conversion_rate}%
            </p>
            <p className="text-xs text-zinc-600 mt-1">{reports.booked_this_month} booked this month</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Revenue Booked</p>
            <p className="text-3xl font-semibold text-[#F5A623]">{formatCurrency(reports.revenue_this_month)}</p>
            <p className="text-xs text-zinc-600 mt-1">This month</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Avg Days to Convert</p>
            <p className="text-3xl font-semibold text-white">
              {reports.avg_days_to_convert !== null ? `${reports.avg_days_to_convert}d` : 'N/A'}
            </p>
            <p className="text-xs text-zinc-600 mt-1">Enquiry to booking</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Value */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#F5A623]" /> Pipeline Value
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(reports.pipeline_value).map(([stage, data]) => {
              const maxCount = Math.max(...Object.values(reports.pipeline_value).map(d => d.count));
              const pct = maxCount > 0 ? (data.count / maxCount) * 100 : 0;
              return (
                <div key={stage} data-testid={`pipeline-${stage}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-zinc-400">{PIPELINE_LABELS[stage] || stage}</span>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="bg-white/5 text-zinc-400 border-white/10 text-xs">{data.count}</Badge>
                      <span className="text-xs text-[#F5A623] w-20 text-right">{formatCurrency(data.value)}</span>
                    </div>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#F5A623]/60 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Leads by Source */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <PieChart className="h-5 w-5 text-[#F5A623]" /> Leads by Source
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {reports.leads_by_source.map(s => (
              <div key={s.source} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded border border-white/5">
                <div>
                  <p className="text-white text-sm font-medium">{s.source}</p>
                  <p className="text-xs text-zinc-500">{s.total} leads</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 font-medium">{s.booked} booked</p>
                  <p className="text-xs text-zinc-500">{s.total > 0 ? Math.round(s.booked / s.total * 100) : 0}% rate</p>
                </div>
              </div>
            ))}
            {reports.leads_by_source.length === 0 && <p className="text-center text-zinc-500 py-4">No data yet</p>}
          </CardContent>
        </Card>

        {/* Lost Breakdown */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Target className="h-5 w-5 text-rose-400" /> Lost Lead Reasons
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {reports.lost_by_reason.map(r => (
              <div key={r.reason} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded border border-white/5">
                <span className="text-zinc-400 text-sm">{r.reason}</span>
                <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">{r.count}</Badge>
              </div>
            ))}
            {reports.lost_by_reason.length === 0 && <p className="text-center text-zinc-500 py-4">No lost leads yet</p>}
          </CardContent>
        </Card>

        {/* Sales Person Performance (Admin/Manager only) */}
        {reports.sales_performance && reports.sales_performance.length > 0 && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-[#F5A623]" /> Team Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {reports.sales_performance.map(sp => (
                <div key={sp.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded border border-white/5" data-testid={`sales-perf-${sp.id}`}>
                  <div>
                    <p className="text-white text-sm font-medium">{sp.name}</p>
                    <p className="text-xs text-zinc-500">{sp.total_leads} leads handled</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-emerald-400 font-medium">{sp.converted}</p>
                      <p className="text-[10px] text-zinc-600">Won</p>
                    </div>
                    <div className="text-center">
                      <p className="text-rose-400 font-medium">{sp.lost}</p>
                      <p className="text-[10px] text-zinc-600">Lost</p>
                    </div>
                    <div className="text-center">
                      <p className={`font-medium ${sp.conversion_rate >= 30 ? 'text-emerald-400' : 'text-amber-400'}`}>{sp.conversion_rate}%</p>
                      <p className="text-[10px] text-zinc-600">Rate</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Digest Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Daily Digest Preview</DialogTitle></DialogHeader>
          <div className="mt-4 rounded-lg overflow-hidden border border-white/10" data-testid="digest-preview-content">
            <iframe
              srcDoc={previewHtml}
              title="Digest Preview"
              className="w-full h-[500px] border-0 bg-black"
              sandbox="allow-same-origin"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowPreview(false)} className="border-white/10 text-white hover:bg-white/5">Close</Button>
            <Button className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" onClick={() => { setShowPreview(false); handleSendDigest(); }} disabled={sending}>
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send to Team
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesReportsPage;
